import React, { useState, useEffect } from "react";
import { useUserAndGameContext } from "../../../contexts/UserAndGameContext";
import { useTiersGameContext } from "../context/TiersGameContext";
import { Typography, Table, TableBody, TableRow, TableCell, TableHead, Box, TableContainer, Paper } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import LoadingScreen from "../../LoadingScreen/LoadingScreen";
import { useGetGolfTournamentLeaderboard } from "../../../hooks/pools/useGetGolfTournamentLeaderboard";
import { DateTime } from 'luxon';
import IntervalDropdown from "../../GamePageComponents/Dropdowns/IntervalDropdown";

const TournamentLeaderboard = () => {
    const { 
        leaderboardData, 
        interval,
        setInterval, 
        intervalInfo, 
    } = useUserAndGameContext();

    const { 
        firstInterval,
        lastInterval,
        theCurrentInterval,
        intervalsTournamentId,
        hasThisIntervalsDeadlinePassed,
    } = useTiersGameContext();

    const { mutate: fetchTournamentLeaderboard, data: tournamentLeaderboardData } = useGetGolfTournamentLeaderboard();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Trigger the mutation when tournament ID changes
    useEffect(() => {
        if (intervalsTournamentId) {
            fetchTournamentLeaderboard(intervalsTournamentId);
        }
    }, [intervalsTournamentId, fetchTournamentLeaderboard]);

    const [isLoading, setIsLoading] = useState(true);
    const [previousTournamentData, setPreviousTournamentData] = useState(null);
    //console.log("TournamentLeaderboard - tournamentLeaderboardData: ", tournamentLeaderboardData, "isLoading: ", isLoading);

    const todayInNewYork = DateTime.now().setZone('America/New_York');
    const tournamentRounds = tournamentLeaderboardData?.Tournament?.Rounds;
    
    // Simple function to determine the current round number
    const getCurrentRoundNumber = () => {
        if (!tournamentRounds || tournamentRounds.length === 0) return 1;
        
        // Format today's date for comparison and ensure correct time zone
        const today = todayInNewYork.startOf('day');
        
        // Check if date is before all tournament days - return round 1
        const firstRoundDate = DateTime.fromISO(tournamentRounds[0].Day).startOf('day');
        if (today < firstRoundDate) return 1;
        
        // Check if date is after all tournament days - return round 4
        const lastRoundDate = DateTime.fromISO(tournamentRounds[tournamentRounds.length - 1].Day).startOf('day');
        if (today > lastRoundDate) return 4;
        
        // Alternative approach: compare by date string instead of DateTime object
        const todayStr = today.toFormat('yyyy-MM-dd');
        
        for (const round of tournamentRounds) {
            // Extract just the date part for comparison
            const roundDateStr = DateTime.fromISO(round.Day).toFormat('yyyy-MM-dd');
            
            if (todayStr === roundDateStr) {
                return round.Number;
            }
        }
        
        console.warn("Unexpected date scenario in tournament round calculation");
        return 1;
    };
    
    // Get current round number
    const currentRound = getCurrentRoundNumber();
    
    // Track and update our display data
    useEffect(() => {
        // Initial load with no previous data
        if (isLoading && !previousTournamentData && tournamentLeaderboardData?.Players) {
            setPreviousTournamentData(tournamentLeaderboardData);
            setIsLoading(false);
        }
        // Data updates when we already have previous data
        else if (tournamentLeaderboardData?.Players) {
            // New data has arrived
            if (previousTournamentData?.TournamentID !== tournamentLeaderboardData?.TournamentID) {
                // Different tournament - show loading
                setIsLoading(true);
                // After a slight delay, update the data
                const timer = setTimeout(() => {
                    setPreviousTournamentData(tournamentLeaderboardData);
                    setIsLoading(false);
                }, 500); // Longer delay for tournament changes
                return () => clearTimeout(timer);
            } else {
                // Same tournament, maybe just refreshed data
                setPreviousTournamentData(tournamentLeaderboardData);
                setIsLoading(false);
            }
        } else {
            // No valid data, show loading
            setIsLoading(true);
        }
    }, [tournamentLeaderboardData]);

    // Count player picks for the current interval
    const countPlayerPicks = (playerId) => {
        if (!leaderboardData || !playerId) return 0;
        
        let pickCount = 0;
        leaderboardData?.forEach(user => {
            // Find the matching interval data for this user
            const intervalData = user?.intervalData?.find(int => int.interval === interval);
            
            if (intervalData) {
                // Check if any of this user's picks match the player ID
                const hasPlayerPick = intervalData.players.some(
                    player => player.id === playerId.toString()
                );
                
                if (hasPlayerPick) {
                    pickCount++;
                }
            }
        });
        
        return pickCount;
    };

    // Function to get the total score for a player
    const getTotalScore = (row) => {
        if (row.IsWithdrawn) {
            return 'WD';
        } if (currentRound > 1 && row.MadeCut === 0) {
            return 'Cut';
        } else if (row.TotalScore === undefined || row.TotalScore === null || row.TotalScore === 0) {
            return 'E';
        } else if (row.TotalScore > 0) {
            return `+${row.TotalScore}`;
        } else {
            return row.TotalScore;
        }
    }

    // Custom styling for cells
    const headerCellStyle = {
        backgroundColor: "#002129",
        color: "#ffffff",
        fontWeight: 700,
        fontSize: isMobile ? "12px" : "16px",
        padding: isMobile ? "8px" : "16px"
    };
    
    const bodyCellStyle = {
        fontWeight: 700,
        fontSize: isMobile ? "12px" : "16px",
        padding: isMobile ? "8px" : "16px"
    };

    const rankCellStyle = {
        ...bodyCellStyle,
        maxWidth: isMobile ? "25px" : "50px",
        width: isMobile ? "25px" : "50px",
    };

    const rankHeaderStyle = {
        ...headerCellStyle,
        maxWidth: isMobile ? "60px" : "100px",
        width: isMobile ? "60px" : "100px",
    };

    // If we have no data at all to display, show the loading screen
    if (!previousTournamentData?.Players) {
        return <LoadingScreen />;
    }

    // Use the data we have - either previous or current
    // The loading overlay will appear when isLoading is true
    return (
        <div style={{ position: 'relative' }}>

            {theCurrentInterval > firstInterval && (
                <IntervalDropdown
                    interval={interval}
                    setInterval={setInterval}
                    firstInterval={firstInterval}
                    lastInterval={lastInterval}
                    theCurrentInterval={theCurrentInterval}
                    intervalInfo={intervalInfo}
                    includeOverall={false}
                />
            )}

            {/* Loading overlay - appears on top of the table during loading */}
            {isLoading && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    zIndex: 10
                }}>
                    <LoadingScreen />
                </div>
            )}
            
            {/* The table is always rendered with the data we have */}
            <TableContainer component={Paper} style={{
                overflowX: "clip", 
                maxWidth:'750px', 
                margin:'0 auto', 
                marginBottom: '20px',
                position: 'relative'
            }}>
                <Table aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell align="center" style={rankHeaderStyle}>Rank</TableCell>
                            <TableCell align="left" style={headerCellStyle}>Player</TableCell>
                            <TableCell align="center" style={headerCellStyle}>Score</TableCell>
                            <TableCell align="center" style={headerCellStyle}>Picked</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {previousTournamentData.Players.map((row, index) => (
                            <TableRow
                                key={index}
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                                <TableCell align="center" style={rankCellStyle}>{row.Rank ?? '-'}</TableCell>
                                <TableCell align="left" style={bodyCellStyle}>{row.Name}</TableCell>
                                <TableCell align="center" style={bodyCellStyle}>{getTotalScore(row)}</TableCell>
                                <TableCell align="center" style={bodyCellStyle}>
                                    {countPlayerPicks(row.PlayerID)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
}

export default TournamentLeaderboard;